import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { Button, Input, Segmented, Space, theme, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import MaintenanceWindowsV2Context from '@totem/components/maintenanceWindowsV2/MaintenanceWindowsV2Context';
import {
  BypassTag,
  StatusTag,
} from '@totem/components/maintenanceWindowsV2/StatusTags';
import { MaintenanceWindow } from '@totem/components/maintenanceWindowsV2/types';
import {
  endTime,
  formatDuration,
  formatSchedule,
  getBuildingLabel,
  getBuildingNames,
  getBypassState,
  getTicketId,
  getWindowStatus,
  isClosed,
  startTime,
  STATUS_LABELS,
} from '@totem/components/maintenanceWindowsV2/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

export type WindowsView = 'all' | 'current' | 'awaiting' | 'bypass' | 'closed';

type Props = {
  view: WindowsView;
  onViewChange: (view: WindowsView) => void;
};

const WindowsTab = ({ view, onViewChange }: Props) => {
  const { loading, filteredData, now, onAction } = useContext(
    MaintenanceWindowsV2Context,
  );
  const { userProfile } = useContext(UserProfileContext);
  const { token } = theme.useToken();
  const [search, setSearch] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(20);

  const canEdit = permissions.maintenanceWindowEditor.hasPermissions(
    userProfile,
    securityGroupCheckConstraintAny,
  );

  const inView = (win: MaintenanceWindow, viewKey: WindowsView) => {
    const status = getWindowStatus(win, now);
    switch (viewKey) {
      case 'current':
        return status !== 'closed';
      case 'awaiting':
        return status === 'awaitingApproval' || status === 'bypassRejected';
      case 'bypass':
        return win.details.requiresBypass;
      case 'closed':
        return status === 'closed';
      default:
        return true;
    }
  };

  const counts = useMemo(() => {
    const views: WindowsView[] = [
      'all',
      'current',
      'awaiting',
      'bypass',
      'closed',
    ];
    return Object.fromEntries(
      views.map((viewKey) => [
        viewKey,
        filteredData.filter((win) => inView(win, viewKey)).length,
      ]),
    ) as Record<WindowsView, number>;
  }, [filteredData, now]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return filteredData
      .filter((win) => inView(win, view))
      .filter(
        (win) =>
          term === '' ||
          [
            win.details.reason,
            win.details.description,
            win.createdBy,
            ...getBuildingNames(win),
            ...(win.devices ?? []).map((dev) => dev.device?.name),
          ]
            .filter(Boolean)
            .some((text) => text.toLowerCase().includes(term)),
      );
  }, [filteredData, view, search, now]);

  const secondary = { color: token.colorTextSecondary, fontSize: 12 };

  const timing = (win: MaintenanceWindow) => {
    const status = getWindowStatus(win, now);
    const length = formatDuration(endTime(win) - startTime(win));
    switch (status) {
      case 'active':
        return `${length} · ${formatDuration(endTime(win) - now)} left`;
      case 'overdue':
        return `${length} · ${formatDuration(now - endTime(win))} past end`;
      case 'closed':
        return length;
      default:
        return `${length} · starts in ${formatDuration(startTime(win) - now)}`;
    }
  };

  const approvalSummary = (win: MaintenanceWindow) => {
    if (!win.details.requiresBypass) {
      return <span style={secondary}>—</span>;
    }
    const winApprovals = win.approvals ?? [];
    const approvedBy = winApprovals.find(
      (approval) => approval.status === 'Approved',
    );
    if (approvedBy) {
      return (
        <Tooltip title={`Approved by ${approvedBy.approver}`}>Approved</Tooltip>
      );
    }
    const rejected = winApprovals.filter(
      (approval) => approval.status === 'Rejected',
    ).length;
    return rejected > 0
      ? `${rejected} of ${winApprovals.length} rejected`
      : `0 of ${winApprovals.length}`;
  };

  const columns: ColumnProps<MaintenanceWindow>[] = [
    {
      title: 'Status',
      key: 'status',
      render: (_, win) => <StatusTag status={getWindowStatus(win, now)} />,
      sorter: (compA, compB) =>
        sortStringAscending(
          STATUS_LABELS[getWindowStatus(compA, now)],
          STATUS_LABELS[getWindowStatus(compB, now)],
        ),
    },
    {
      title: 'Window',
      key: 'reason',
      render: (_, win) => {
        const ticketId = getTicketId(win);
        return (
          <>
            <Link to={`/dashboard/maintenanceWindows2/${win.id}`}>
              {win.details.reason || 'Maintenance window'}
            </Link>
            {(win.details.description || ticketId) && (
              <div style={secondary}>
                {win.details.description}
                {win.details.description && ticketId ? ' · ' : ''}
                {ticketId && (
                  <Link to={`/dashboard/ticket/${ticketId}`}>Ticket</Link>
                )}
              </div>
            )}
          </>
        );
      },
      sorter: (compA, compB) =>
        sortStringAscending(compA.details.reason, compB.details.reason),
    },
    {
      title: 'Building',
      key: 'building',
      render: (_, win) => (
        <Tooltip title={getBuildingNames(win).join(', ')}>
          {getBuildingLabel(win)}
        </Tooltip>
      ),
      sorter: (compA, compB) =>
        sortStringAscending(getBuildingLabel(compA), getBuildingLabel(compB)),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_, win) => (
        <>
          {formatSchedule(win)}
          <div style={secondary}>{timing(win)}</div>
        </>
      ),
      defaultSortOrder: 'descend',
      sorter: (compA, compB) =>
        sortNumberAscending(startTime(compA), startTime(compB)),
    },
    {
      title: 'Devices',
      key: 'devices',
      align: 'right',
      render: (_, win) => (win.devices ?? []).length,
      sorter: (compA, compB) =>
        sortNumberAscending(
          (compA.devices ?? []).length,
          (compB.devices ?? []).length,
        ),
    },
    {
      title: 'Firewall bypass',
      key: 'bypass',
      render: (_, win) => <BypassTag state={getBypassState(win, now)} />,
    },
    {
      title: 'Approval',
      key: 'approvals',
      render: (_, win) => approvalSummary(win),
    },
    {
      title: 'Alerts suppressed',
      key: 'suppressed',
      align: 'right',
      render: (_, win) =>
        (win.blockedEvents ?? []).length > 0 ? (
          (win.blockedEvents ?? []).length
        ) : (
          <span style={secondary}>—</span>
        ),
      sorter: (compA, compB) =>
        sortNumberAscending(
          (compA.blockedEvents ?? []).length,
          (compB.blockedEvents ?? []).length,
        ),
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: (compA, compB) =>
        sortStringAscending(compA.createdBy, compB.createdBy),
    },
  ];

  if (canEdit) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 84,
      render: (_, win) =>
        !isClosed(win) && (
          <Tooltip title="Edit">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              aria-label="Edit"
              onClick={() => onAction('edit', win)}
            />
          </Tooltip>
        ),
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <Segmented<WindowsView>
          value={view}
          onChange={onViewChange}
          options={[
            { value: 'current', label: `Active & upcoming ${counts.current}` },
            {
              value: 'awaiting',
              label: `Awaiting approval ${counts.awaiting}`,
            },
            { value: 'bypass', label: `Firewall bypass ${counts.bypass}` },
            { value: 'closed', label: `Closed ${counts.closed}` },
            { value: 'all', label: `All ${counts.all}` },
          ]}
        />
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search reason, building, device"
            style={{ width: 280 }}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Space>
      </div>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey={(win) => win.id}
        scroll={{ x: true }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
          showTotal: (total) => `${total} windows`,
        }}
      />
    </div>
  );
};

export default WindowsTab;
